import React, { useState } from 'react'

import { alpha, useTheme, Box, Stack, Typography, Collapse } from '@mui/material'
import { ChevronDownIcon } from 'lucide-react'
import { Card } from '@repo/ui'

import type { ActivationTier } from '@repo/types'
import { hasActivationTiers, resolveActivationTier } from '@repo/types'
import { toDollar } from 'utils'

// Direct path, not the `components` barrel — this component is itself re-exported
// from that barrel, so importing through it would be a cycle (same reason
// `InfoBlock` imports it this way).
import { InfoTooltip } from '../../feedback/InfoTooltip'

interface ActivationPricingScheduleProps {
  /**
   * 'table' — the full schedule, always visible. This is the ONLY place the
   * schedule should render in full — the rate is identical across every plan
   * that shares a metered price (see `tiers` + `sameAcrossPlans` below), so it
   * belongs once, on the compare-plans page, below the plan cards — never
   * repeated per card.
   *
   * 'summary' — a single line plus an expander that reveals the same rows.
   * Used inside denser surfaces (e.g. the Billing overview activations card)
   * where an 8-row table would be a pricing-page artifact, not a dashboard
   * element.
   */
  variant?: 'table' | 'summary'
  /**
   * The graduated activation-pricing schedule to render (VRG-375 — read from
   * the plan's `activation_tiers`, off the API, not a hardcoded constant).
   * `null`/empty renders NOTHING, in either variant (AC4) — a wrong or
   * missing price is worse than no price. Required, not fetched internally,
   * so both call sites stay in control of which plan's schedule they're
   * showing.
   */
  tiers: ActivationTier[] | null
  /**
   * The company's current-period activation count. When provided, the row
   * matching the current band is highlighted — gives the Billing meter's
   * "next band" circles the "where does this end" context they lack alone.
   */
  currentCount?: number
  /** Render the heading + description above the rows (table variant only). */
  showHeading?: boolean
  /**
   * table variant only. Whether every plan's schedule is identical —
   * computed by the caller via `activationTiersAgree` across ALL plans, since
   * this component only ever sees the one schedule it's asked to render. The
   * heading's "The same on every plan." claim is only assertable when this is
   * true (VRG-375 AC5). Defaults to true for callers that only ever show a
   * single schedule (e.g. the Billing overview summary) and never make a
   * cross-plan claim in the first place.
   */
  sameAcrossPlans?: boolean
  sx?: object
}

/**
 * Shared renderer for a graduated activation-pricing schedule, so the
 * compare-plans page and the Billing overview card render the SAME rows off
 * the SAME `tiers` prop and cannot drift (working-conventions §28/§47 — build
 * out the shared library rather than hand-rolling the same numbers twice).
 */
export const ActivationPricingSchedule = ({
  variant = 'table',
  tiers,
  currentCount,
  showHeading = true,
  sameAcrossPlans = true,
  sx,
}: ActivationPricingScheduleProps) => {
  const theme = useTheme()
  const [expanded, setExpanded] = useState(false)

  // AC4 — no usable schedule (sync hasn't resolved tiers, or this plan has
  // none): render nothing, in either variant, rather than fall back to a
  // default schedule.
  if (!hasActivationTiers(tiers)) return null

  const entryRate = tiers[0].rate
  const topRate = tiers[tiers.length - 1].rate
  /** Upper bound of the entry band — the "first N" in the graduated explanation. */
  const firstBandUpper = tiers[0].upTo
  const currentTier = currentCount != null ? resolveActivationTier(currentCount, tiers) : null

  /**
   * The "a month with N activations is charged…" illustration, built from the schedule
   * rather than written into the copy.
   *
   * Needs three bands whose first two are bounded — it spans two full bands and lands in
   * a third. Returns null otherwise, and the sentence is dropped: a graduated schedule
   * with fewer bands is adequately described by the table beneath it, and inventing an
   * example that does not match the rows would be worse than having none.
   *
   * The count sits one band past the second boundary, so it genuinely reaches the third
   * rate whatever the boundaries are.
   */
  const workedExample = (() => {
    if (tiers.length < 3) return null
    const [first, second, third] = tiers
    if (first.upTo === null || second.upTo === null) return null
    return {
      count: second.upTo + (second.upTo - first.upTo),
      firstRate: first.rate,
      firstUpTo: first.upTo,
      secondRate: second.rate,
      secondSize: second.upTo - first.upTo,
      thirdRate: third.rate,
    }
  })()

  const rows = (
    // No rounded/clipped wrapper: the matrix's rows sit directly on the Card with
    // divider borders doing the separation, and a rounded container around these made
    // the schedule read as an inset panel rather than a sibling table.
    <Box>
      {tiers.map((tier, index) => {
        const lowerBound = index === 0 ? 0 : (tiers[index - 1].upTo ?? 0)
        // `currentTier.upperBound` is the matching row's own `upTo` (set from
        // this same `tier` object inside `resolveActivationTier`), so compare
        // on that rather than object identity.
        const isCurrent = currentTier != null && currentTier.upperBound === tier.upTo
        const rangeLabel = tier.upTo !== null
          ? `${lowerBound.toLocaleString()}–${tier.upTo.toLocaleString()}`
          : `${lowerBound.toLocaleString()}+`

        return (
          <Stack
            // `tier.upTo` is `null` on the final unbounded band, so it can't
            // stand alone as the key — fall back to the index for that one row.
            key={tier.upTo ?? index}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              // Matches PlanComparisonMatrix's DATA rows: transparent, a `divider` top
              // border, `py: 3`. Earlier this tinted every row with `primary[50]`, which
              // is what that matrix uses for its SECTION HEADERS — so the schedule read
              // as eight consecutive section headings sitting under a table of plain
              // rows. The first row takes no border because the heading block above
              // already separates it.
              //
              // `px: 6` (24px), symmetric. It previously had `pr` alone, which was
              // invisible on the seven transparent rows but not on the CURRENT one: that
              // row is tinted, so with no left padding its text sat flush against the
              // tint's own edge.
              //
              // 24 and not 12: it is the leftmost-column gutter the shared `Table` gives
              // every data table, and it is also what the stat tiles below this block are
              // inset by (measured — the label lands at 610 against their 611). At 12 the
              // rows sat at 598, between the summary line above and the tiles below,
              // aligned to neither.
              py: 3,
              px: 6,
              borderTop:
                index === 0 ? 'none' : `1px solid ${theme.palette.divider}`,
              backgroundColor: isCurrent
                ? alpha(theme.palette.brand[500], 0.08)
                : undefined,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography
                variant="body3"
                color={isCurrent ? 'text.primary' : 'text.secondary'}
                fontWeight={isCurrent ? 600 : 400}
              >
                {`${rangeLabel} activations / mo`}
              </Typography>
              {isCurrent && (
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.brand[500], fontWeight: 600 }}
                >
                  Your current tier
                </Typography>
              )}
            </Stack>
            {/* body3 / text.primary — the matrix uses body3 for BOTH its labels
                (text.secondary) and its values (text.primary), so the two tables share
                one type scale. This was body2, which made every rate a size larger than
                anything in the table above it. */}
            <Typography
              variant="body3"
              color={isCurrent ? undefined : 'text.primary'}
              fontWeight={isCurrent ? 600 : 400}
              sx={{ color: isCurrent ? theme.palette.brand[500] : undefined }}
            >
              {`${toDollar(tier.rate, true)} each`}
            </Typography>
          </Stack>
        )
      })}
    </Box>
  )

  if (variant === 'table') {
    // Inside a Card, with the heading at the top — the same shape as
    // `PlanComparisonMatrix`'s "Compare plans" card it sits directly beneath, so the
    // two read as sibling sections rather than a card followed by loose content.
    // Title is `body1`/`text.primary`, matching that card's heading exactly.
    return (
      <Card sx={{ mt: 8, ...sx }}>
        {showHeading && (
          <Stack spacing={1} sx={{ mb: 4 }}>
            <Typography variant="body1" color="text.primary">
              {/* AC5 — "The same on every plan." is only assertable once it's
                  checkable across every plan's own schedule, which this single-
                  schedule component can't do on its own; the caller decides via
                  `activationTiersAgree` and passes the answer down. */}
              {sameAcrossPlans
                ? 'Activation pricing. The same on every plan.'
                : 'Activation pricing.'}
            </Typography>
            {/* No maxWidth: the description runs the full width of the card, as the
                surrounding cards' copy does. Capping it at 640 left a ragged short
                column against a much wider table. */}
            <Typography
              variant="body3"
              color="text.secondary"
              sx={{ lineHeight: 1.5 }}
            >
              Every plan bills certificate activations on this graduated schedule.
              Each tier is charged at its own rate, so a month is billed as a blend
              rather than at one flat price.
              {/* The worked example is DERIVED, and omitted when it cannot be built.
                  It used to index tiers[1] and tiers[2] directly and name a hardcoded
                  "600 activations" — safe against a fixed 8-band constant, not against
                  a schedule that now arrives from the API, where fewer than three bands
                  would crash the page. The count is also derived rather than fixed:
                  "600" only illustrates three bands while the boundaries happen to be
                  100 and 500, so a re-priced schedule would have kept the sentence
                  grammatical while making its arithmetic wrong. */}
              {workedExample && (
                <>
                  {' '}A month with {workedExample.count.toLocaleString()} activations is
                  charged at {toDollar(workedExample.firstRate, true)} for the first{' '}
                  {workedExample.firstUpTo.toLocaleString()}, then{' '}
                  {toDollar(workedExample.secondRate, true)} for the next{' '}
                  {workedExample.secondSize.toLocaleString()}, then{' '}
                  {toDollar(workedExample.thirdRate, true)} for the rest.
                </>
              )}{' '}
              Plans differ only in how many activations are included free.
            </Typography>
          </Stack>
        )}
        {rows}
      </Card>
    )
  }

  return (
    <Stack spacing={2} sx={sx}>
      {/* space-between, not spacing — the control sits hard against the card's right
          edge. Laid out as a spaced row the expander landed wherever the sentence
          happened to end, roughly mid-card, which made this the only element on the
          card with a ragged right edge: the tier bar above it and the stat tiles below
          it both span the full width. It also matches what this card's own header
          already does — "Activations" left, "Activated: On sale" hard right. */}
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setExpanded((previous) => !previous)}
        sx={{ cursor: 'pointer' }}
      >
        {/* "Graduated", NOT "volume" — these are different billing models and the
            difference is what a brand actually pays. Under VOLUME pricing every
            activation is charged at the rate of the band you land in. Under GRADUATED
            pricing each band charges only for the activations inside it, so a month is
            billed as a blend. Stripe holds this as a graduated price and
            `graduatedActivationValue` computes it per band, so "volume" was the wrong
            word for the thing we actually charge.

            That distinction is the one thing this line CANNOT afford to spell out —
            it is deliberately short (see below), so "graduated" arrives as a bare
            adjective doing load-bearing work. Hence the tooltip: the definition is
            one hover away, read from the glossary rather than lengthened into the
            line (§44). Only the SUMMARY variant gets it; the table variant states
            the same thing in prose directly above its rows. */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body3" color="text.secondary">
            {/* Short enough not to read as a paragraph. The card is dense — a meter, a
                tier bar and four stat tiles — and at 95 characters the previous copy was
                the longest run of prose on it, which is what made a quiet body3 line feel
                loud. "As your total grows" still carries the GRADUATED behaviour (rates
                fall as volume rises) rather than just naming the endpoints; the worked
                example lives in the expanded table. This line only has to make someone
                want to open it. */}
            {`Graduated pricing: ${toDollar(entryRate, true)} down to ${toDollar(topRate, true)} as your total grows.`}
          </Typography>
          {/* The whole row toggles the expander, and a React portal propagates
              through the REACT tree rather than the DOM one — so without this a
              click on the info glyph, or on the tooltip's own footer link, would
              also open/close the schedule underneath it. Contained here rather
              than lifted onto the row so the row stays entirely clickable, which
              is what makes the sentence itself an affordance. */}
          <Box
            component="span"
            onClick={(e) => e.stopPropagation()}
            sx={{ display: 'inline-flex' }}
          >
            <InfoTooltip term="graduated-activation-rate" />
          </Box>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            variant="body3"
            sx={{
              // text.primary, not brand — this is a quiet "show me more" on a dense
              // billing card, and a purple link pulled the eye away from the meter and
              // the numbers, which are what the card is actually about. Underline
              // carries the affordance; colour does not need to as well.
              color: theme.palette.text.primary,
              textDecoration: 'underline',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            {expanded ? 'Hide schedule' : 'See full schedule'}
          </Typography>
          <ChevronDownIcon
            size={14}
            strokeWidth={1.5}
            color={theme.palette.text.primary}
            style={{
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s',
            }}
          />
        </Stack>
      </Stack>
      <Collapse in={expanded}>
        <Box sx={{ pt: 2 }}>{rows}</Box>
      </Collapse>
    </Stack>
  )
}
